import invariant from 'tiny-invariant'
import uniq from 'lodash/fp/uniq'
import { useAtom, useAtomValue } from 'jotai'

import { Button, Flex } from '@sequencehq/core-components'
import { applyNewMinimumToPhases } from '@sequencehq/utils'
import {
  useMinimumEditorRoot,
  MinimumEditorDomainProvider,
  MinimumEditorMode,
  MinimumEditorForm,
  minimumEditorCubeAdapter,
  useMinimumEditorDomainContext
} from 'Cube/view/common/drawers/minimumEditor'
import { scheduleCurrencyAtom } from '../../../atoms/reviewPricing.atoms'
import {
  Phase,
  pricingDetailsAtom
} from '../../../atoms/reviewPricing.primitives.atoms'
import { getPricesForPhase, updatePhase } from '../../../utils/phases'
import {
  pricingUtilsMinimumAdapter,
  pricingUtilsPhaseAdapter
} from '../reviewPricingDrawers.adapters'
import { MinimumEditorRootProps } from 'Cube/view/common/drawers/minimumEditor/drawer/minimumEditor.types'
import { match } from 'ts-pattern'
import { StepDrawer } from '../../../../components/StepDrawer'

type Props = {
  phaseId: string
  minimumId: string | undefined
  onDismiss: () => void
}

export const MinimumEditorDrawer = ({
  phaseId,
  minimumId,
  onDismiss
}: Props) => {
  const currency = useAtomValue(scheduleCurrencyAtom)
  const [pricingDetails, setPricingDetails] = useAtom(pricingDetailsAtom)

  const phase = pricingDetails.phases.find(phase => phase.id === phaseId)
  invariant(phase, 'phase is undefined')

  const existingMinimum = phase.minimums.find(
    minimum => minimum.id === minimumId
  )

  const allPricesForPhase = getPricesForPhase({
    phase,
    prices: pricingDetails.prices
  })

  const availablePrices = allPricesForPhase.filter(
    price => 'usageMetricId' in price.structure
  )

  const applyToAllAvailable = (() => {
    const allRecurringFrequencies = uniq(
      availablePrices
        .map(price =>
          price.billingFrequency !== 'ONE_TIME'
            ? price.billingFrequency
            : undefined
        )
        .filter(Boolean)
    )

    return allRecurringFrequencies.length < 2
  })()

  const { domain } = useMinimumEditorRoot({
    existingMinimum: existingMinimum
      ? minimumEditorCubeAdapter.in(
          pricingUtilsMinimumAdapter.in(existingMinimum)
        )
      : null,
    applyToAllAvailable,
    availablePrices,
    mode: minimumId ? MinimumEditorMode.EDIT : MinimumEditorMode.CREATE,
    currency,
    onClose: () => {
      onDismiss()
    },
    onSave: minimum => {
      const newMinimum = minimumEditorCubeAdapter.out(minimum)

      /**
       * Only the phase being edited is passed, so the update stops there.
       */
      const updates = applyNewMinimumToPhases({
        newMinimum,
        phaseId,
        phases: [
          pricingUtilsPhaseAdapter.in({ phase, prices: pricingDetails.prices })
        ]
      })

      const updatedPhases: Phase[] = updatePhase({
        phases: pricingDetails.phases,
        phaseId,
        update: phaseToUpdate => ({
          ...phaseToUpdate,
          minimums: (
            updates.updatedPhases[phaseId]?.minimumIds ??
            phaseToUpdate.minimums.map(({ id }) => id)
          ).map(minimumId => {
            const updatedMinimum = updates.updatedMinimums[minimumId]

            if (!updatedMinimum) {
              const existingMinimum = phaseToUpdate.minimums.find(
                minimum => minimum.id === minimumId
              )
              invariant(existingMinimum, 'no existing minimum')
              return existingMinimum
            }

            return pricingUtilsMinimumAdapter.out(updatedMinimum)
          })
        })
      })

      setPricingDetails({ phases: updatedPhases })

      onDismiss()
    }
  })

  return (
    <MinimumEditorDomainProvider value={domain}>
      <MinimumEditorDrawerContent />
    </MinimumEditorDomainProvider>
  )
}

const getDrawerTitle = (mode: MinimumEditorRootProps['mode']) => {
  return match(mode)
    .with(MinimumEditorMode.CREATE, () => 'Add minimum')
    .with(MinimumEditorMode.VIEW, () => 'View minimum')
    .with(MinimumEditorMode.EDIT, () => 'Edit minimum')
    .exhaustive()
}

const getSaveText = (mode: MinimumEditorRootProps['mode']) => {
  return match(mode)
    .with(MinimumEditorMode.CREATE, () => 'Add minimum')
    .with(MinimumEditorMode.VIEW, () => 'Update minimum')
    .with(MinimumEditorMode.EDIT, () => 'Update minimum')
    .exhaustive()
}

const MinimumEditorDrawerContent = () => {
  const context = useMinimumEditorDomainContext()

  return (
    <StepDrawer
      data-testid="reviewPricing.drawers.minimumEditor"
      title={getDrawerTitle(context.queries.editor.mode)}
      onClose={context.mutators.close}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={context.mutators.close}
            data-testid="reviewPricing.drawers.minimumEditor.cancel"
          >
            Cancel
          </Button>
          <Button
            data-testid="reviewPricing.drawers.minimumEditor.save"
            variant="primary"
            onClick={context.mutators.save}
            disabled={!context.queries.editor.canSave}
          >
            {getSaveText(context.queries.editor.mode)}
          </Button>
        </>
      }
    >
      <Flex gap="16px" flexDir="column">
        <MinimumEditorForm />
      </Flex>
    </StepDrawer>
  )
}
